import React, { useCallback, useMemo, useState } from 'react';
import { CSSObjectWithLabel, MenuPlacement } from 'react-select';
import isArray from 'lodash/isArray';

import Input from '@xcritical/input';
import { ISelectedFilterComponent } from '@xcritical/filter';

import { ISelectOption } from '@crm-framework/types';
import { TextTruncate } from '@crm-framework/utils';
import { FilterElement } from '@crm-framework/dashboard';
import { withError } from '@crm-framework/validation';

import { ManagersSelect } from './ManagersSelect';

const getTopPositionForMenu = (
  currentTopPosition?: (string | number) | (string | number)[],
  element?: null | HTMLDivElement
) => {
  if (currentTopPosition && element) {
    const { top, height } = element.getBoundingClientRect();
    const maxTopPosition = top + height;

    return currentTopPosition > maxTopPosition
      ? maxTopPosition
      : currentTopPosition;
  }

  return currentTopPosition;
};

const SelectOption: React.FC<ISelectOption> = ({ label }) => (
  <TextTruncate title={label}>{label}</TextTruncate>
);

const ManagersSelectWithError = withError(ManagersSelect);
const InputWithError = withError(Input);

const managerFilterFormatterBase =
  (isGrouped: boolean) =>
  (_filterType: string, filterFieldId: string): FilterElement =>
  (
    value: any,
    onChange: (value: any) => void,
    condition?: string,
    _validationError?: ISelectedFilterComponent['validationError'],
    tagConditionsRef?: ISelectedFilterComponent['tagConditionsRef']
  ) => {
    const [inputValue, setInputValue] = useState('');

    const onInputChange = useCallback(
      (newInputValue: string, { action }: { action: string }): boolean => {
        if (action === 'set-value') return false;

        if (action === 'input-change') {
          setInputValue(newInputValue);
        }

        return true;
      },
      []
    );

    const onMenuClose = useCallback(() => {
      setInputValue('');
    }, []);

    const isMulti = ['in', 'notIn'].some(
      (multiCondition) => multiCondition === condition
    );
    // eslint-disable-next-line react/destructuring-assignment
    const selectValue = isArray(value) && !isMulti ? value[0] : value;

    const selectStyles = useMemo(
      () => ({
        menuPortal: (css: CSSObjectWithLabel) => ({
          ...css,
          zIndex: 300,
          // eslint-disable-next-line no-undef
          top: getTopPositionForMenu(css.top, tagConditionsRef?.current),
        }),
      }),
      [tagConditionsRef]
    );

    if (condition === 'contains') {
      return (
        <InputWithError
          disabled={!condition}
          shouldFitContainer
          onChange={onChange}
          value={value ?? ''}
          invalid={!!_validationError}
          errorProps={{ message: _validationError }}
        />
      );
    }

    const saveInputTextProps = {
      inputValue,
      onInputChange,
      onMenuClose,
      menuPlacement: 'auto' as MenuPlacement,
    };

    return (
      <ManagersSelectWithError
        isSearchable
        shouldFitContainer
        closeMenuOnSelect={!isMulti}
        value={selectValue}
        isMulti={isMulti}
        appearance="filters-value-select"
        menuPosition="fixed"
        placeholder="Search..."
        styles={selectStyles}
        menuPortalTarget={document.body}
        formatOptionLabel={SelectOption}
        dictionaryName={filterFieldId}
        disableSelectHeader={isMulti}
        onChange={onChange}
        hideSelectedOptions={false}
        isGrouped={isGrouped}
        errorProps={{ message: _validationError }}
        {...saveInputTextProps}
      />
    );
  };

export const managerFilterFormatter = managerFilterFormatterBase(false);
export const groupedManagerFilterFormatter = managerFilterFormatterBase(true);
